/**
 * Studio-built landing / static page by slug (Page Builder Phase 4).
 *
 * Rendering and props are shared with `/` (pages/index.tsx), which shows the
 * page flagged "Set as Home": see components/page/StudioPage.tsx and
 * lib/pageBuilder/studioPageProps.ts.
 */
import type { GetServerSideProps } from 'next'
import StudioPage from '@/components/page/StudioPage'
import { loadStudioPageProps } from '@/lib/pageBuilder/studioPageProps'
import { requestedLocale } from '@/lib/localization'
import { fetchPagePreviewSchema, fetchPageSchema } from '@/lib/studioApi'

export const getServerSideProps: GetServerSideProps = async ctx => {
	const slug = String(ctx.params?.slug ?? '')
	const locale = requestedLocale({ locale: ctx.locale, query: ctx.query })
	const previewToken =
		typeof ctx.query.studio_preview === 'string' ? ctx.query.studio_preview : ''
	if (previewToken) {
		ctx.res.setHeader('Cache-Control', 'private, no-store, max-age=0')
		ctx.res.setHeader('Referrer-Policy', 'no-referrer')
	}

	try {
		const props = await loadStudioPageProps(ctx, {
			locale,
			label: slug,
			preview: Boolean(previewToken),
			fetchPage: () =>
				previewToken
					? fetchPagePreviewSchema(slug, previewToken, locale)
					: fetchPageSchema(slug, locale),
		})
		// A missing or unpublished page is a 404.
		return props ? { props } : { notFound: true }
	} catch {
		// No schema (connectivity / misconfig) → nothing to render.
		return { notFound: true }
	}
}

export default StudioPage
